import React, { useState, useMemo } from 'react';
import { useAudience } from '../context/AudienceContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Calculator,
  Copy,
  ChevronDown,
  ChevronUp,
  Search,
  Info,
  RotateCcw,
  Brain,
  Heart,
  Activity,
  Baby,
  Stethoscope,
  Droplets,
  Flame,
  Syringe,
  Bone,
  Scale,
  Thermometer,
  Beaker,
  User,
  Target,
  Pill,
  Wind
} from 'lucide-react';

// Import all medical scores from data files
import { ALL_MEDICAL_SCORES, SCORE_CATEGORIES } from '../data/medicalScores';
import { EXTENDED_SCORES } from '../data/medicalScoresExtended';
import { ADDITIONAL_SPECIALTY_SCORES } from '../data/medicalScoresSpecialty';
import { CARDIOLOGY_MORE_SCORES, INTERNAL_MEDICINE_SCORES, NUTRITION_SCORES, NURSING_SCORES, MORE_SPECIALTY_SCORES } from '../data/medicalScoresMore';
import { CARDIOLOGY_EXTRA_SCORES } from '../data/medicalScoresCardioExtra';
import { GASTRO_EXTENDED_SCORES } from '../data/medicalScoresGastroExtended';
import { PSYCHIATRY_SCORES } from '../data/medicalScoresPsychiatry';
import { PEDIATRIC_EXTRA_SCORES, ORL_SCORES, SPORT_MEDICINE_SCORES, PEDIATRIC_ORL_SPORT_SCORES } from '../data/medicalScoresPediaOrlSport';
import { INFECTIOUS_EXTENDED_SCORES, EMERGENCY_EXTENDED_SCORES, ICU_EXTRA_SCORES, INFECTIOUS_EMERGENCY_ICU_SCORES } from '../data/medicalScoresInfectiousEmergencyICU';
import { SCORES_PART_1 } from '../data/medicalScoresComplete1';
import { SCORES_PART_2 } from '../data/medicalScoresComplete2';
import { SCORES_PART_3 } from '../data/medicalScoresComplete3';
import { SCORES_PART_4 } from '../data/medicalScoresComplete4';
import { SCORES_PART_5 } from '../data/medicalScoresComplete5';
import { SCORES_PART_6 } from '../data/medicalScoresComplete6';
import { SCORES_PART_7 } from '../data/medicalScoresComplete7';
import { SCORES_PART_8 } from '../data/medicalScoresComplete8';
import { SCORES_PART_9 } from '../data/medicalScoresComplete9';
import { SCORES_PART_10 } from '../data/medicalScoresComplete10';
import { SCORES_PART_11 } from '../data/medicalScoresComplete11';
import { SCORES_PART_12 } from '../data/medicalScoresComplete12';
import { SCORES_PART_13 } from '../data/medicalScoresComplete13';
import { SCORES_PART_14 } from '../data/medicalScoresComplete14';
import { SCORES_PART_15 } from '../data/medicalScoresComplete15';

// Merge all scores from all files
const MEDICAL_SCORES = { 
  ...ALL_MEDICAL_SCORES, 
  ...EXTENDED_SCORES,
  ...ADDITIONAL_SPECIALTY_SCORES,
  // More specialty scores
  ...CARDIOLOGY_MORE_SCORES,
  ...INTERNAL_MEDICINE_SCORES,
  ...NUTRITION_SCORES,
  ...NURSING_SCORES,
  ...MORE_SPECIALTY_SCORES,
  // Extra cardio
  ...CARDIOLOGY_EXTRA_SCORES,
  // Gastro extended
  ...GASTRO_EXTENDED_SCORES,
  // Psychiatry
  ...PSYCHIATRY_SCORES,
  // Pediatrics, ORL, Sport
  ...PEDIATRIC_EXTRA_SCORES,
  ...ORL_SCORES,
  ...SPORT_MEDICINE_SCORES,
  ...PEDIATRIC_ORL_SPORT_SCORES,
  // Infectious, Emergency, ICU
  ...INFECTIOUS_EXTENDED_SCORES,
  ...EMERGENCY_EXTENDED_SCORES,
  ...ICU_EXTRA_SCORES,
  ...INFECTIOUS_EMERGENCY_ICU_SCORES,
  // Complete parts 1-10
  ...SCORES_PART_1,
  ...SCORES_PART_2,
  ...SCORES_PART_3,
  ...SCORES_PART_4,
  ...SCORES_PART_5,
  ...SCORES_PART_6,
  ...SCORES_PART_7,
  ...SCORES_PART_8,
  ...SCORES_PART_9,
  ...SCORES_PART_10,
  // Parts 11-12 - Additional scores
  ...SCORES_PART_11,
  ...SCORES_PART_12,
  ...SCORES_PART_13,
  ...SCORES_PART_14,
  ...SCORES_PART_15
};

// Icon mapping for categories
const CATEGORY_ICONS = {
  'Anesthésie': Syringe,
  'Brûlés': Flame,
  'Cardiologie': Heart,
  'Chirurgie': Bone,
  'Gériatrie': User,
  'Hématologie': Droplets,
  'Hépato-Gastro': Beaker,
  'Infectiologie': Thermometer,
  'Néphrologie': Droplets,
  'Neurologie': Brain,
  'Obstétrique': Baby,
  'Oncologie': Target,
  'ORL': Stethoscope,
  'Orthopédie': Bone,
  'Pédiatrie': Baby,
  'Pneumologie': Wind,
  'Psychiatrie': Brain,
  'Réanimation': Activity,
  'Rhumatologie': Bone,
  'Toxicologie': Pill,
  'Urgences': Stethoscope
};

// Get all unique categories
const getAllCategories = () => {
  const categories = {};
  Object.values(MEDICAL_SCORES).forEach(score => {
    if (!categories[score.category]) {
      categories[score.category] = 0;
    }
    categories[score.category]++;
  });
  return Object.entries(categories).sort((a, b) => a[0].localeCompare(b[0]));
};

export default function ScoresPage() {
  const { isAdult } = useAudience();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedScore, setSelectedScore] = useState(null);
  const [values, setValues] = useState({});
  const [result, setResult] = useState(null);
  const [expandedInfo, setExpandedInfo] = useState(false);

  const categories = useMemo(() => getAllCategories(), []);
  const totalScores = Object.keys(MEDICAL_SCORES).length;

  const filteredScores = useMemo(() => {
    return Object.entries(MEDICAL_SCORES).filter(([key, score]) => {
      const matchesSearch = score.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           score.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           score.category.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || score.category === selectedCategory;
      // Filter pediatric/obstetric scores based on audience toggle
      const isPediatric = score.category === 'Pédiatrie' || score.category === 'Obstétrique';
      const matchesAudience = isAdult ? !isPediatric : true;
      return matchesSearch && matchesCategory && matchesAudience;
    });
  }, [searchQuery, selectedCategory, isAdult]);

  const handleValueChange = (fieldId, value) => {
    setValues(prev => ({ ...prev, [fieldId]: value }));
    setResult(null);
  };

  const calculateScore = () => {
    if (!selectedScore) return;
    const score = MEDICAL_SCORES[selectedScore];
    const calculated = score.calculate(values);
    const interpretation = score.interpret(calculated.score, calculated, values);
    setResult({ ...calculated, interpretation });
  };

  const copyResult = () => {
    if (!result || !selectedScore) return;
    const score = MEDICAL_SCORES[selectedScore];
    const text = `${score.name}: ${result.score}${result.max && typeof result.max !== 'string' ? '/' + result.max : result.max ? ' ' + result.max : ''} - ${result.interpretation.text}. ${result.interpretation.detail}`;
    navigator.clipboard.writeText(text);
    toast.success('Résultat copié');
  };

  const resetScore = () => {
    setValues({});
    setResult(null);
  };

  const colorClasses = {
    success: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    warning: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    danger: 'bg-red-500/20 text-red-400 border-red-500/30'
  };

  const currentScore = selectedScore ? MEDICAL_SCORES[selectedScore] : null;
  const ScoreIcon = currentScore?.icon || Calculator;
  const CategoryIcon = currentScore ? (CATEGORY_ICONS[currentScore.category] || Stethoscope) : null;

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Calculator className="w-6 h-6 text-cyan-400" />
          Scores Médicaux
        </h1>
        <p className="text-[hsl(var(--text-dim))]">
          <span className="text-cyan-400 font-semibold">{totalScores}</span> scores disponibles • 
          <span className="text-cyan-400 font-semibold"> {categories.length}</span> spécialités • 
          Mode {isAdult ? 'adulte' : 'pédiatrique'}
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Left Panel - Score Selection */}
        <div className="md:col-span-1 space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted))]" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher un score..."
              className="pl-10 bg-transparent"
              data-testid="score-search"
            />
          </div>

          {/* Category Filter */}
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="bg-transparent">
              <SelectValue placeholder="Toutes catégories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes catégories ({filteredScores.length})</SelectItem>
              {categories.map(([cat, count]) => (
                <SelectItem key={cat} value={cat}>
                  {cat} ({count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Score List */}
          <ScrollArea className="h-[55vh] pr-2">
            <div className="space-y-2">
              {filteredScores.map(([key, score]) => {
                const Icon = score.icon || Calculator;
                return (
                  <button
                    key={key}
                    onClick={() => {
                      setSelectedScore(key);
                      setValues({});
                      setResult(null);
                    }}
                    className={`w-full text-left p-3 rounded-lg border transition-all ${
                      selectedScore === key
                        ? 'bg-cyan-400/20 border-cyan-400/50'
                        : 'bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))] hover:border-cyan-400/30'
                    }`}
                    data-testid={`score-item-${key}`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                      <span className="font-medium text-sm truncate">{score.name}</span>
                    </div>
                    <p className="text-xs text-[hsl(var(--text-dim))] mt-1 line-clamp-1">{score.description}</p>
                    <Badge variant="outline" className="mt-2 text-xs">{score.category}</Badge>
                  </button>
                );
              })}
              {filteredScores.length === 0 && (
                <p className="text-center text-[hsl(var(--muted))] py-8">Aucun score trouvé</p>
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Right Panel - Calculator */}
        <div className="md:col-span-2">
          {currentScore ? (
            <div className="glass-card rounded-xl p-6 space-y-6">
              {/* Score Header */}
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-semibold flex items-center gap-2">
                    <ScoreIcon className="w-5 h-5 text-cyan-400" />
                    {currentScore.name}
                  </h2>
                  <p className="text-sm text-[hsl(var(--text-dim))]">{currentScore.description}</p>
                </div>
                <Badge variant="outline" className="text-cyan-400 border-cyan-400/30 flex items-center gap-1">
                  {CategoryIcon && <CategoryIcon className="w-3 h-3" />}
                  {currentScore.category}
                </Badge>
              </div>

              {/* Formula Info */}
              <button
                onClick={() => setExpandedInfo(!expandedInfo)}
                className="flex items-center gap-2 text-sm text-cyan-400 hover:text-cyan-300"
              >
                <Info className="w-4 h-4" />
                <span>Formule</span>
                {expandedInfo ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {expandedInfo && (
                <div className="p-3 rounded-lg bg-cyan-400/10 border border-cyan-400/20 text-sm font-mono whitespace-pre-wrap">
                  {currentScore.formula}
                </div>
              )}

              {/* Input Fields */}
              <ScrollArea className="h-[35vh] pr-4">
                <div className="space-y-4">
                  {currentScore.fields.map((field) => (
                    <div key={field.id}>
                      <label className="block text-sm font-medium text-[hsl(var(--text-dim))] mb-2">
                        {field.label}
                        {field.points !== undefined && (
                          <span className="ml-2 text-cyan-400">({field.points > 0 ? '+' : ''}{field.points} pts)</span>
                        )}
                      </label>
                      
                      {field.type === 'select' && (
                        <Select
                          value={values[field.id]?.toString() || ''}
                          onValueChange={(val) => handleValueChange(field.id, parseFloat(val))}
                        >
                          <SelectTrigger className="bg-transparent" data-testid={`score-field-${field.id}`}>
                            <SelectValue placeholder="Sélectionner..." />
                          </SelectTrigger>
                          <SelectContent>
                            {field.options.map((opt, idx) => (
                              <SelectItem key={`${opt.value}-${idx}`} value={opt.value.toString()}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}

                      {field.type === 'checkbox' && (
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={values[field.id] || false}
                            onCheckedChange={(checked) => handleValueChange(field.id, checked)}
                            data-testid={`score-field-${field.id}`}
                          />
                          <span className="text-sm">Oui</span>
                        </div>
                      )}

                      {field.type === 'number' && (
                        <Input
                          type="number"
                          min={field.min}
                          max={field.max}
                          step={field.step || 1}
                          value={values[field.id] || ''}
                          onChange={(e) => handleValueChange(field.id, parseFloat(e.target.value) || 0)}
                          className="bg-transparent"
                          data-testid={`score-field-${field.id}`}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>

              {/* Calculate Button */}
              <div className="flex gap-2">
                <Button
                  onClick={calculateScore}
                  className="flex-1 bg-cyan-400/90 text-black font-medium hover:bg-cyan-300 glow-cyan"
                  data-testid="score-calculate-btn"
                >
                  <Calculator className="w-5 h-5 mr-2" />
                  Calculer
                </Button>
                <Button variant="outline" onClick={resetScore}>
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset
                </Button>
              </div>

              {/* Result */}
              {result && (
                <div className={`p-4 rounded-lg border ${colorClasses[result.interpretation.level]} animate-scale-in`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-3xl font-bold font-mono" data-testid="score-result">
                        {result.score}
                        {result.max && typeof result.max === 'number' && (
                          <span className="text-lg opacity-60">/{result.max}</span>
                        )}
                        {result.max && typeof result.max === 'string' && result.max !== 'variable' && (
                          <span className="text-lg opacity-60 ml-1">{result.max}</span>
                        )}
                      </div>
                      <div className="font-semibold mt-1">{result.interpretation.text}</div>
                      <div className="text-sm mt-2 opacity-90">{result.interpretation.detail}</div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={copyResult}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="glass-card rounded-xl p-8 text-center h-full flex flex-col items-center justify-center">
              <Calculator className="w-16 h-16 text-[hsl(var(--muted))] mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Sélectionnez un score</h3>
              <p className="text-[hsl(var(--text-dim))] mb-4">
                Choisissez un score parmi les {totalScores} disponibles
              </p>
              <div className="flex flex-wrap gap-2 justify-center max-w-md">
                {categories.slice(0, 8).map(([cat, count]) => {
                  const Icon = CATEGORY_ICONS[cat] || Stethoscope;
                  return (
                    <Badge 
                      key={cat} 
                      variant="outline" 
                      className="cursor-pointer hover:bg-cyan-400/20"
                      onClick={() => setSelectedCategory(cat)}
                    >
                      <Icon className="w-3 h-3 mr-1" />
                      {cat}
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Disclaimer */}
      <div className="glass-card rounded-lg p-4 border border-amber-500/30">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-[hsl(var(--text-dim))]">
            Les scores sont fournis à titre indicatif pour aide à la décision clinique. 
            Toujours confronter au contexte clinique et aux protocoles locaux.
          </p>
        </div>
      </div>
    </div>
  );
}
